<script setup lang="ts">
import { computed } from 'vue';

export interface SwitchProps {
  id: string;
  label?: string;
  modelValue?: boolean;
  disabled?: boolean;
  wrapperClass?: string;
  labelClass?: string;
}

const props = withDefaults(defineProps<SwitchProps>(), {
  label: undefined,
  modelValue: false,
  disabled: false,
  wrapperClass: undefined,
  labelClass: undefined,
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  change: [value: boolean];
}>();

const labelId = computed(() => `${props.id}-label`);

const toggle = () => {
  if (props.disabled) return;
  const next = !props.modelValue;
  emit('update:modelValue', next);
  emit('change', next);
};
</script>

<template>
  <div :class="['inline-flex items-center gap-2.5', wrapperClass]">
    <button
      :id="id"
      type="button"
      role="switch"
      :aria-checked="modelValue ? 'true' : 'false'"
      :aria-labelledby="label || $slots.label ? labelId : undefined"
      :disabled="disabled"
      :class="[
        'relative inline-flex h-7 w-12 shrink-0 items-center rounded-full',
        'transition-colors duration-200 ease-in-out',
        'focus:outline-2 focus:outline-text-primary focus:outline-offset-2',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        modelValue ? 'bg-actions-primary' : 'bg-actions-tertiary-active',
      ]"
      @click="toggle"
    >
      <span
        :class="[
          'inline-block h-5 w-5 transform rounded-full bg-white shadow',
          'transition-transform duration-200 ease-in-out',
          modelValue ? 'translate-x-6' : 'translate-x-1',
        ]"
      ></span>
    </button>
    <label
      v-if="label || $slots.label"
      :id="labelId"
      :class="[
        'select-none text-base font-semibold',
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
        labelClass,
      ]"
      @click="toggle"
    >
      <slot name="label">{{ label }}</slot>
    </label>
  </div>
</template>
